/**
 * Analytics Service provides multiple methods for analytics tracking.
 * Tracking methods trigger required details to Google analytics service
 */

import GoogleAnalyticsService from './google-analytics-service';

const googleAnalyticsService = new GoogleAnalyticsService();

class AnalyticsService {
  /**
   * Track product impressions
   * @param  {object}   dataObj contains product details for PDP
   * @return {object}          null
   */
  trackImpressions = (dataObj, callback) => {
    let impressions = [];
    let currencyCode = '';

    if (dataObj && dataObj.price) {
      currencyCode = dataObj.price.currencyIso; // Local currency
      impressions = [
        {
          name: dataObj.name,
          id: dataObj.code,
          price: dataObj.price.value,
          brand: '',
          category: '',
          variant: '',
          list: '',
          position: '',
        },
      ];
      googleAnalyticsService.trackImpressions(
        impressions,
        currencyCode,
        callback,
      );
    }
  };

  /**
   * Track product detail impressions
   * @param  {object}   dataObj contains product details for PDP
   * @return {object}          null
   */
  trackDetailImpressions = (dataObj, callback) => {
    let impressions = [];

    if (dataObj && dataObj.price) {
      impressions = [
        {
          name: dataObj.name,
          id: dataObj.code,
          price: dataObj.price.value,
          brand: '',
          category: '',
          variant: '',
        },
      ];
      googleAnalyticsService.trackDetailImpressions(impressions, callback);
    }
  };

  /**
   * Track search results information
   * @param  {object}   dataObj contains search term and total result
   * @return {object}          null
   */
  trackSearchResults = (dataObj, callback) => {
    let productsSearched = {};

    if (dataObj && dataObj.pagination) {
      productsSearched = {
        searchTerm: dataObj.freeTextSearch,
        totalSearchResults: dataObj.pagination.totalResults,
      };
      googleAnalyticsService.trackSearchResults(productsSearched, callback);
    }
  };

  /**
   * Track add to cart products related information
   * @param  {object}   dataObj contains details of product added to cart
   * @return {object}          null
   */
  trackAddToCart = (dataObj, callback) => {
    let productAdded = {};

    if (dataObj) {
      productAdded = {
        products: [
          {
            id: dataObj.code,
            name: dataObj.name,
            quantity: dataObj.quantity,
          },
        ],
      };
      googleAnalyticsService.trackAddToCart(productAdded, callback);
    }
  };

  /**
   * Track product removed from the cart related information
   * @param  {object}   dataObj contains details of product removed from cart
   * @return {object}          null
   */
  trackRemoveFromCart = (dataObj, callback) => {
    let productRemoved = {};

    if (dataObj) {
      productRemoved = {
        products: [
          {
            id: dataObj.code,
            name: dataObj.name,
            quantity: dataObj.quantity,
          },
        ],
      };
      googleAnalyticsService.trackRemoveFromCart(productRemoved, callback);
    }
  };

  /**
   * Track products purchased related information
   * @param  {object}   transactionObj contains details of the transaction
   * @param  {object}   dataObj contains details of product purchased
   * @return {object}          null
   */
  trackProductPurchased = (dataObj, callback) => {
    let productPurchased = {};
    const products = [];
    const orderPromotions = [];

    if (dataObj) {
      if (dataObj.appliedOrderPromotions) {
        dataObj.appliedOrderPromotions.forEach((promotion) => {
          promotion.appliedCouponCodes.forEach((coupon) => {
            orderPromotions.push(coupon);
          });
        });
      }
      dataObj.entries.forEach((product) => {
        const productPromotions = [];
        if (dataObj.appliedProductPromotions) {
          dataObj.appliedProductPromotions.forEach((promotion) => {
            productPromotions.push(promotion.promotion.code);
          });
        }
        products.push({
          name: product.product.name,
          id: product.product.code,
          price: product.basePrice.value,
          brand: '',
          category: '',
          variant: '',
          quantity: product.quantity,
          coupon: productPromotions.toString(),
        });
      });
      productPurchased = {
        actionField: {
          id: dataObj.code, // Transaction ID. Required for purchases and refunds.
          affiliation: '',
          revenue: dataObj.totalPrice.value, // Total transaction value (incl. tax and shipping)
          tax: dataObj.totalTax.value,
          shipping: dataObj.deliveryCost ? dataObj.deliveryCost.value : 0,
          coupon: orderPromotions.toString(),
        },
        products,
      };
      googleAnalyticsService.trackProductsPurchased(productPurchased, callback);
    }
  };

  /**
   * Track checkout related information
   * @param  {object}   dataObj contains details of product during checkout
   * @return {object}          null
   */
  trackCheckout = (dataObj, callback) => {
    let checkoutProducts = {};
    const products = [];

    if (dataObj) {
      dataObj.forEach((product) => {
        products.push({
          name: product.product.name,
          id: product.product.code,
          price: product.basePrice.value,
          brand: '',
          category: '',
          variant: '',
          quantity: product.quantity,
          coupon: '',
        });
      });
      checkoutProducts = {
        products,
      };
      googleAnalyticsService.trackCheckout(checkoutProducts, callback);
    }
  };

  /**
   * Track marketing form post related information
   * @param  {object}   dataObj contains details of form post
   * @return {object}          null
   */
  trackMarketingFormPost = (dataObj, callback) => {
    googleAnalyticsService.trackMarketingFormPost(dataObj, callback);
  };
}

// export default AnalyticsService;
export default AnalyticsService;



// WEBPACK FOOTER //
// ./src/components/common/services/analytics-service.js